import { zodResolver } from '@hookform/resolvers/zod'
import { Pencil } from 'lucide-react'
import type { ForwardRefExoticComponent, ComponentProps } from 'react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { paths } from 'src/config/paths'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/shared/components/ui/miscellaneous/tooltip'
import { ResponsivePopup } from 'src/shared/components/ui/popups/responsive-popup'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useMyLicenses } from 'src/shared/features/users/api/licenses/use-my-licenses'
import { useUpdateLicenses } from 'src/shared/features/users/api/licenses/use-update-licenses'
import deniedIcon from 'src/shared/features/users/assets/denied.png'
import infoRequestedIcon from 'src/shared/features/users/assets/info.png'
import pendingIcon from 'src/shared/features/users/assets/pending.png'
import { licenseNumberSchema } from 'src/shared/features/users/schemas/licenses'
import type { State } from 'src/shared/features/users/schemas/states'
import { statesMap } from 'src/shared/features/users/schemas/states'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useToast } from 'src/shared/hooks/use-toast'

type StatusIconType = string | ForwardRefExoticComponent<ComponentProps<typeof VerificationBadge>>

interface LicenseStatus {
  text: string
  icon: StatusIconType
  iconProps?: ComponentProps<typeof VerificationBadge>
  iconColorClass: string
  isComponent: boolean
}

const getLicenseStatusPriority = (status: string): number => {
  switch (status) {
    case 'approved':
      return 1
    case 'information_requested':
      return 2
    case 'denied':
      return 3
    case 'pending':
      return 4
    default:
      return 5
  }
}

const updateLicenseSchema = z.object({
  licenseNumber: licenseNumberSchema,
})

type UpdateLicenseFormData = z.infer<typeof updateLicenseSchema>

interface UpdateLicenseNumberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentLicenseNumber: string
  onSubmit: (licenseNumber: string) => void
}

const UpdateLicenseNumberDialog = ({
  open,
  onOpenChange,
  currentLicenseNumber,
  onSubmit,
}: UpdateLicenseNumberDialogProps) => {
  const form = useForm<UpdateLicenseFormData>({
    resolver: zodResolver(updateLicenseSchema),
    defaultValues: {
      licenseNumber: currentLicenseNumber,
    },
  })

  const handleSubmit = (data: UpdateLicenseFormData) => {
    onSubmit(data.licenseNumber)
    onOpenChange(false)
  }

  return (
    <ResponsivePopup open={open} onOpenChange={onOpenChange} title="Update license" trigger={<div />}>
      <div className="p-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <InputFormField
              control={form.control}
              name="licenseNumber"
              label="License number"
              placeholder="e.g. 5431813"
            />
            <Button type="submit" className="w-full">
              Save changes
            </Button>
          </form>
        </Form>
      </div>
    </ResponsivePopup>
  )
}

export const MyLicensesSection = () => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { data: myLicenses } = useMyLicenses()
  const { data: userData } = useMyUserData()
  const updateLicenses = useUpdateLicenses()
  const { toast } = useToast()
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const isValidState = (stateCode: string): stateCode is State => {
    return Object.keys(statesMap).includes(stateCode)
  }

  if (!myLicenses || myLicenses.length === 0) return null

  const isLoanOfficer = userData?.profession === 'mortgage_loan_officer'
  const hasVerifiedLicense = myLicenses.some((license) => license.status === 'approved')

  const getLicenseStatus = (status: string): LicenseStatus => {
    switch (status) {
      case 'approved':
        return {
          text: 'Verified',
          icon: VerificationBadge,
          iconProps: { isVerified: true },
          iconColorClass: 'text-success',
          isComponent: true,
        }
      case 'pending':
        return { text: 'Pending', icon: pendingIcon, iconColorClass: 'text-warning', isComponent: false }
      case 'information_requested':
        return { text: 'Info Requested', icon: infoRequestedIcon, iconColorClass: 'text-warning', isComponent: false }
      case 'denied':
        return { text: 'Denied', icon: deniedIcon, iconColorClass: 'text-destructive', isComponent: false }
      default:
        return {
          text: 'Unverified',
          icon: VerificationBadge,
          iconProps: { isVerified: false },
          iconColorClass: 'text-muted',
          isComponent: true,
        }
    }
  }

  const validLicenses = myLicenses
    .filter((license) => isValidState(license.state))
    .sort((a, b) => getLicenseStatusPriority(a.status) - getLicenseStatusPriority(b.status))

  const displayedLicenses = showAll ? validLicenses : validLicenses.slice(0, 5)
  const hasMoreLicenses = validLicenses.length > 5

  const handleUpdateLicenseNumber = async (newLicenseNumber: string) => {
    if (!myLicenses) return

    try {
      await updateLicenses.mutateAsync(newLicenseNumber)

      toast({
        title: 'Success',
        description: 'License number updated successfully.',
      })
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Failed to update license number. Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <section className="mt-6 space-y-6">
      {isLoanOfficer && myLicenses[0].licenseNumber && (
        <div className="border-t pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-bold">License</h3>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-row gap-2">
              <h3 className="text-base">NMLS# {myLicenses[0].licenseNumber}</h3>
              <button
                onClick={() => setIsUpdateDialogOpen(true)}
                className="flex items-center gap-1 text-sm text-muted hover:text-primary"
              >
                <Pencil className="size-4" />
              </button>
            </div>
            <div className="flex items-center gap-1">
              {isMobile ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <VerificationBadge
                        isVerified={hasVerifiedLicense}
                        className={`size-5 ${hasVerifiedLicense ? 'text-primary' : 'text-muted'}`}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{hasVerifiedLicense ? 'Verified license' : 'Unverified'}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <>
                  <VerificationBadge
                    isVerified={hasVerifiedLicense}
                    className={`size-5 ${hasVerifiedLicense ? 'text-primary' : 'text-muted'}`}
                  />
                  <span>{hasVerifiedLicense ? 'Verified license' : 'Unverified'}</span>
                </>
              )}
            </div>
          </div>
          <UpdateLicenseNumberDialog
            open={isUpdateDialogOpen}
            onOpenChange={setIsUpdateDialogOpen}
            currentLicenseNumber={myLicenses[0].licenseNumber || ''}
            onSubmit={handleUpdateLicenseNumber}
          />
        </div>
      )}

      <div className="border-t pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold">Licensed state(s)</h3>
          <button
            onClick={() => navigate(paths.users.editLicenses.getHref())}
            className="flex items-center gap-1 text-sm text-muted hover:text-primary"
          >
            <Pencil className="size-4" />
            <span>Edit states</span>
          </button>
        </div>
        <div className="flex flex-col gap-4">
          {displayedLicenses.map((license) => (
            <div key={license.state} className="flex items-center justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <p>{statesMap[license.state].label}</p>
                {userData?.profession !== 'mortgage_loan_officer' && (
                  <p className="text-sm font-medium text-muted">{license.licenseNumber}</p>
                )}
              </div>
              {(() => {
                const status = getLicenseStatus(license.status)
                const StatusIcon = status.icon as StatusIconType
                return (
                  <div className="flex items-center gap-2">
                    {isMobile ? (
                      <TooltipProvider>
                        <Tooltip clickDuration={750}>
                          <TooltipTrigger>
                            {status.isComponent ? (
                              <StatusIcon className={`size-4 ${status.iconColorClass}`} {...status.iconProps} />
                            ) : (
                              <img
                                src={status.icon as string}
                                alt={status.text}
                                className="size-4"
                                width={16}
                                height={16}
                              />
                            )}
                          </TooltipTrigger>
                          <TooltipContent>{status.text}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <>
                        {status.isComponent ? (
                          <StatusIcon className={`size-4 ${status.iconColorClass}`} {...status.iconProps} />
                        ) : (
                          <img
                            src={status.icon as string}
                            alt={status.text}
                            className="size-4"
                            width={16}
                            height={16}
                          />
                        )}
                        <p className="text-sm font-medium">{status.text}</p>
                      </>
                    )}
                  </div>
                )
              })()}
            </div>
          ))}
          {hasMoreLicenses && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-2 text-sm font-medium text-primary hover:text-primary/80"
            >
              {showAll ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

export default MyLicensesSection
